import React from 'react';
import { motion } from 'framer-motion';
import { 
  DocumentTextIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  CalendarIcon,
  UserIcon,
  TagIcon,
  FolderIcon,
  LockClosedIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';

const DocumentList = ({ documents, onView, onDownload, onApprove, user }) => {
  const getCategoryColor = (category) => {
    const colors = {
      policy: 'bg-blue-100 text-blue-800',
      form: 'bg-green-100 text-green-800',
      template: 'bg-purple-100 text-purple-800',
      procedure: 'bg-orange-100 text-orange-800',
      guideline: 'bg-indigo-100 text-indigo-800',
      other: 'bg-gray-100 text-gray-800'
    };
    return colors[category] || colors.other;
  };

  const getFileIcon = (mimeType) => {
    if (mimeType.includes('pdf')) return '📄';
    if (mimeType.includes('word')) return '📝';
    if (mimeType.includes('excel')) return '📊';
    if (mimeType.includes('image')) return '🖼️';
    return '📄';
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (documents.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
        <DocumentTextIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No documents found</h3>
        <p className="text-gray-500">No documents match your current filters.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">Documents ({documents.length})</h3>
      </div>
      
      <div className="divide-y divide-gray-200">
        {documents.map((document, index) => (
          <motion.div
            key={document.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
            className="px-6 py-4 hover:bg-gray-50 transition-colors duration-200"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4 flex-1">
                {/* File Icon */}
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                    <span className="text-lg">{getFileIcon(document.mimeType)}</span>
                  </div>
                </div>

                {/* Document Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <h4 className="text-sm font-medium text-gray-900 truncate">
                      {document.title}
                    </h4>
                    {document.isPublic ? (
                      <GlobeAltIcon className="w-4 h-4 text-green-600" title="Public Document" />
                    ) : (
                      <LockClosedIcon className="w-4 h-4 text-gray-400" title="Private Document" />
                    )}
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                    {document.description || 'No description available'}
                  </p>
                  
                  <div className="flex items-center space-x-4 text-xs text-gray-500">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(document.category)}`}>
                      {document.category}
                    </span>
                    {document.subcategory && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {document.subcategory}
                      </span>
                    )}
                    <span className="flex items-center">
                      <CalendarIcon className="w-3 h-3 mr-1" />
                      {formatDate(document.createdAt)}
                    </span>
                    <span className="flex items-center">
                      <UserIcon className="w-3 h-3 mr-1" />
                      {document.CreatedBy?.firstName} {document.CreatedBy?.lastName}
                    </span>
                    <span>{formatFileSize(document.fileSize)}</span>
                    <span className="flex items-center">
                      <EyeIcon className="w-3 h-3 mr-1" />
                      {document.viewCount} views
                    </span>
                    <span className="flex items-center">
                      <ArrowDownTrayIcon className="w-3 h-3 mr-1" />
                      {document.downloadCount} downloads
                    </span>
                  </div>

                  {/* Tags */}
                  {document.tags && document.tags.length > 0 && (
                    <div className="flex items-center space-x-2 mt-2">
                      <TagIcon className="w-3 h-3 text-gray-400" />
                      <div className="flex flex-wrap gap-1">
                        {document.tags.map((tag, tagIndex) => (
                          <span
                            key={tagIndex}
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center space-x-2 ml-4">
                <button
                  onClick={() => onView(document)}
                  className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors duration-200"
                  title="View Document"
                >
                  <EyeIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => onDownload(document)}
                  className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-md transition-colors duration-200"
                  title="Download Document"
                >
                  <ArrowDownTrayIcon className="w-4 h-4" />
                </button>
                {(user?.Role?.name === 'hr' || user?.Role?.name === 'admin' || user?.Role?.name === 'super_admin') && document.requiresApproval && document.status === 'pending' && (
                  <button
                    onClick={() => onApprove(document)}
                    className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors duration-200"
                    title="Approve Document"
                  >
                    ✓
                  </button>
                )}
              </div>
            </div>

            {/* Version Info */}
            {document.version && (
              <div className="mt-2 text-xs text-gray-500">
                Version {document.version}
                {document.publishedAt && (
                  <span className="ml-2">
                    • Published {formatDate(document.publishedAt)}
                  </span>
                )}
              </div>
            )}

            {/* Expiry Warning */}
            {document.expiryDate && new Date(document.expiryDate) < new Date() && (
              <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
                <p className="text-xs text-red-700">
                  ⚠️ This document expired on {formatDate(document.expiryDate)}
                </p>
              </div>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default DocumentList; 